import React, { useMemo } from 'react';
import {
  DataGrid,
  GridColDef,
  CellTypes,
  DataGridProps,
  Icon,
} from '@fashionnovateam/nova-ui-toolkit';
import numeral from 'numeral';

interface Payment {
  bills: unknown[];
  credits: unknown[];
  document_number: string;
  id: string;
  internal_id: string;
  payment_date: string;
  payment_pdf: string;
  total: number;
  vendor_internal_id: number;
}

type PaymentRow = {
  id: string;
  payment_date: CellTypes.DataGridCellDateTimeParams;
  invoice_number: CellTypes.DataGridCellStringParams;
  total: CellTypes.DataGridCellStringParams;
  download: CellTypes.DataGridCellIconButtonParams;
};

const columns: GridColDef[] = [
  {
    field: 'payment_date',
    headerName: 'Date',
    render: 'dateTime',
    flex: 1,
    sortable: true,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'invoice_number',
    headerName: 'Document #',
    render: 'string',
    display: 'flex',
    flex: 3,
    sortable: false,
    minWidth: 150,
    disableColumnMenu: true,
  },
  {
    field: 'total',
    headerName: 'Amount',
    display: 'flex',
    render: 'string',
    flex: 1,
    sortable: true,
    minWidth: 100,
    disableColumnMenu: true,
  },
  {
    field: 'download',
    headerName: '',
    render: 'iconButton',
    display: 'flex',
    flex: 1,
    sortable: false,
    align: 'center',
    minWidth: 50,
    disableColumnMenu: true,
  },
];

type Props = {
  rows: Payment[];
  count: number;
  loading: boolean;
} & Omit<DataGridProps<PaymentRow>, 'columns'>;

function BillPaymentsDataGrid({
  rows,
  count,
  loading,
  ...datagridProps
}: Props) {
  const transformedRows = useMemo(
    () =>
      rows.map(
        (payment) =>
          ({
            id: payment.id,
            payment_date: {
              activity: false,
              time: false,
              date: payment.payment_date,
              timeZone: 'UTC',
            },
            invoice_number: payment.document_number,
            total: `${numeral(payment.total).format('$0,0.00')}`,
            download: {
              onClick: () => window.open(payment.payment_pdf, '_blank'),
              color: 'secondary',
              label: 'download',
              icon: <Icon name="download/download-down" />,
            },
          }) satisfies PaymentRow,
      ),
    [rows],
  );

  return (
    <DataGrid
      stateName="payment_datagrid"
      hideFilterButton
      hideGroupButton
      columns={columns}
      loading={loading}
      rows={transformedRows}
      initialState={{
        pinnedColumns: {
          left: ['payment_date'],
        },
      }}
      sortingMode="server"
      paginationMode="server"
      rowCount={count || 0}
      pageSizeOptions={[15]}
      {...datagridProps}
    />
  );
}

export default BillPaymentsDataGrid;
